import React, { memo } from 'react';
import moment from 'moment';
import { isNil, isString, capitalize, upperFirst, isEqual } from 'lodash';
import PropTypes from 'prop-types';

import SSPList from '../../../common/components/SSPList/index';
import { LLFormat, iotOnDateFormat } from '../../../common/services/ValidationRules';
import { CHARGER_STATUS_ALARM, CHARGER_STATUS_ALERT, statusLabels } from '../../../common/constants/statuses';
import StringUtils from '../../../common/services/StringUtils';
import { sortingLowerCase } from '../../../common/services/I2P2Helper';
import uiLabelsPaths from '../../../common/services/uiFieldLabelsDictionary';

import {
  onDatePickerFilter,
  onTextFilterNosaving as onTextFilter,
  onTextOrDatePickerFilter,
} from '../../../common/components/CommonTableDataFilter/CommonTableDataFilter';

import { TableSelect } from '../../ChargersList/components/ChargersTableComponents';
import { getCountryByCode } from '../../AdminTools/components/CountryUtils';
import MenuItem from '../../../common/components/MenuItem/MenuItem';
import MappedLabel from '../../../common/components/MappedLabel/MappedLabel';
import { allMenuItem } from '../../../common/components/CommonTableColumns';
import { StatusLabel } from '../../../common/constants/CommonUsedDropdownsStatics';

const whereOptions = [
  { value: 'UI', label: 'UI' },
  { value: 'DEVICE', label: 'Device' },
  { value: 'API', label: 'API' },
  { value: 'IOT_ON', label: 'IoT.ON' },
  { value: 'HUBJECT', label: 'Hubject' },
  { value: 'AWS', label: 'AWS' },
];

const whereValueLabelMap = whereOptions.reduce((acc, { value, label }) => {
  acc[value] = label;

  return acc;
}, {});

const renderDefaultStringOrStatusCell = (value) => {
  let label = value;

  if (value.toLowerCase() === 'ok') label = 'OK';
  if (StringUtils.includesMultipleStrings(value, ['linkError', 'internalError'])) label = upperFirst(value);

  return <div>{label}</div>;
};

const renderStateCell = (value) => <StatusLabel status={value} />;

const renderWarrantyEndCell = (value) => {
  const convertedValue = parseInt(value, 10);

  return value && moment.utc(convertedValue).isValid() ? moment.utc(convertedValue).format(LLFormat) : '';
};

const renderConditionCell = (value) => <StatusLabel status={value} />;

const renderOnlineCell = (value) => <StatusLabel status={value} />;

const convertBoolValue = (value) => {
  if (value === 'true') {
    value = 'Yes';
  }
  if (value === 'false') {
    value = 'No';
  }

  return value;
};

const cellRender = (path, value, valueConverted) => {
  value = convertBoolValue(value);
  valueConverted = convertBoolValue(valueConverted);

  if (value) {
    if (/i2p2_state\/ports\[\d\]\/p_status/.test(path)) {
      // uiLabelsPaths.ports_0_p_status 0-3...
      return renderStateCell(value);
    }
    switch (path) {
      case uiLabelsPaths.c_condition: {
        return renderConditionCell(value);
      }
      case uiLabelsPaths.c_warranty_end: {
        return renderWarrantyEndCell(value);
      }
      case uiLabelsPaths.c_status: {
        return renderStateCell(value);
      }
      case uiLabelsPaths.c_country: {
        return getCountryByCode(value);
      }
      case uiLabelsPaths.c_online:
      case uiLabelsPaths.changed_online_status: {
        return renderOnlineCell(value);
      }
      default: {
        return isNil(valueConverted)
          ? ' '
          : isString(valueConverted)
            ? renderDefaultStringOrStatusCell(valueConverted)
            : parseFloat(valueConverted).toString();
      }
    }
  }

  return '';
};

export const activitiesTabTableId = 'activities';

const getColumns = (isModal = false) => {
  const originSelectItems = [
    allMenuItem,
    ...whereOptions
      .sort((a, b) => sortingLowerCase(a.label, b.label))
      .map(({ value, label }) => (
        <MenuItem key={value} title={label} value={value}>
          {label}
        </MenuItem>
      )),
  ];

  return [
    {
      Header: 'Date/Time (UTC)',
      accessor: 'timestamp',
      minWidth: 200,
      Cell: ({ value }) => moment(value).utc().format(iotOnDateFormat),
      Filter: onDatePickerFilter,
    },
    {
      Header: 'Source/User',
      accessor: 'user_name',
      minWidth: 200,
      Filter: onTextFilter,
    },
    {
      Header: 'Where',
      accessor: 'origin',
      minWidth: 100,
      Cell: ({ value }) => whereValueLabelMap[value],
      Filter: ({ filter, onChange }) => <TableSelect options={originSelectItems} onChange={onChange} value={filter ? filter.value : ''} />,
    },
    {
      id: 'field',
      Header: 'Field/Action Name',
      accessor: 'field',
      minWidth: 150,
      Filter: onTextFilter,
      Cell: ({ original: { field } }) => {
        const splitted = field.split(': ');

        return (
          <>
            <MappedLabel>{splitted[0]}</MappedLabel>
            {splitted[1] && <span>:{splitted[1]}</span>}
          </>
        );
      },
    },
    {
      id: 'old_value',
      Header: 'Old Value',
      accessor: 'old_value',
      minWidth: 250,
      Filter: ({ filter, onChange }) => onTextOrDatePickerFilter({ filter, onChange, isModal }),
      Cell: ({ original: { field_path: fieldPath, old_value: oldValue }, value }) => cellRender(fieldPath, oldValue, value),
    },
    {
      id: 'new_value',
      Header: 'New Value/Action Result',
      accessor: 'new_value',
      minWidth: 250,
      className: 'break-line-cell',
      Filter: ({ filter, onChange }) => onTextOrDatePickerFilter({ filter, onChange, isModal }),
      Cell: ({ original: { field_path: fieldPath, new_value: newValue }, value }) => cellRender(fieldPath, newValue, value),
    },
  ];
};

const ActivitiesTab = memo(
  ({ isMobile, isModal, onGetDataAction, exportLimit, onExport }) => {
    const isMobileClass = isMobile ? ' mobile' : '';
    const isModalClass = isModal ? ' modal' : '';
    const containerClassName = `create-edit-static-list${isMobileClass}${isModalClass}`;
    const columns = getColumns(isModal);

    return (
      <div data-activetab="activities" className={containerClassName}>
        <SSPList
          isModal={isModal}
          mobileSticky
          exportLimit={exportLimit}
          id={activitiesTabTableId}
          onGetDataAction={onGetDataAction}
          defaultSorted={{ id: 'timestamp', desc: true }}
          defaultPageSize={20}
          columns={columns}
          clearFilterOnInit
          multiselect
          exportable
          onExport={onExport}
        />
      </div>
    );
  },
  (prevProps, nextProps) =>
    isEqual(prevProps.isMobile, nextProps.isMobile) &&
    isEqual(prevProps.isModal, nextProps.isModal) &&
    isEqual(prevProps.charger, nextProps.charger) &&
    isEqual(prevProps.config, nextProps.config),
);

ActivitiesTab.propTypes = {
  isMobile: PropTypes.bool,
  isModal: PropTypes.bool,
  onGetDataAction: PropTypes.func,
};

export default ActivitiesTab;
